import { useState, useEffect, useMemo, useCallback } from 'react';
import { analytics } from '@eti/utils';
import { Stack } from '@etg/wings';
import { m as motion, AnimatePresence } from 'framer-motion';
import { findIndexOfSegment, findNextAvailableSegment, getNextSegment, flatBounds, isLastAvailableSegment, getSegment } from '../utils/seatMapContentUtils';
import SeatSummary from './summary/components/SeatSummary';
import SeatMapHeader from './SeatMapHeader';
import SeatMapMainContent from './SeatMapMainContent';
import Footer from './SeatMapFooter';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const SeatMapContent = _ref => {
  var _firstAvailableSegmen;
  let {
    bounds = [],
    onClose,
    onChange,
    getSelected,
    totalMarkup,
    totalPrice,
    selectedSeats,
    onRemove = () => {}
  } = _ref;
  const flattenedBounds = useMemo(() => flatBounds(bounds), [bounds]);
  const firstAvailableSegment = getNextSegment(flattenedBounds);
  const getInitialActiveTravelerId = travelerIds => (travelerIds === null || travelerIds === void 0 ? void 0 : travelerIds[0]) || 0;
  const [activeSegment, setActiveSegment] = useState((_firstAvailableSegmen = firstAvailableSegment === null || firstAvailableSegment === void 0 ? void 0 : firstAvailableSegment.boundSegmentIndex) !== null && _firstAvailableSegmen !== void 0 ? _firstAvailableSegmen : '0');
  const [activeTraveler, setActiveTraveler] = useState(getInitialActiveTravelerId(firstAvailableSegment === null || firstAvailableSegment === void 0 ? void 0 : firstAvailableSegment.travelerIds));
  const [isSummaryShowing, setIsSummaryShowing] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const handleSegmentChange = useCallback(segment => {
    const {
      boundSegmentIndex,
      travelerIds
    } = segment;
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'SEGMENTS',
      seatingCurrentSegment: findIndexOfSegment(flattenedBounds, boundSegmentIndex) + 1
    });
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(getInitialActiveTravelerId(travelerIds));
    setIsSummaryShowing(false);
  }, [flattenedBounds]);
  const handleNextSegmentClick = useCallback(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'CONTINUE'
    });
    if (isSummaryShowing) {
      return;
    }
    const isLastAvailableSegmentActive = isLastAvailableSegment(flattenedBounds, activeSegment);
    if (isLastAvailableSegmentActive) {
      setIsSummaryShowing(true);
    } else {
      const nextSegment = findNextAvailableSegment(flattenedBounds, activeSegment);
      if (nextSegment) {
        handleSegmentChange(nextSegment);
      }
    }
    const nextSegment = findNextAvailableSegment(flattenedBounds, activeSegment);
    if (nextSegment) {
      handleSegmentChange(nextSegment);
    }
    setShouldAnimate(true);
  }, [activeSegment, flattenedBounds, handleSegmentChange, isSummaryShowing]);
  const getCurrentSegmentHeader = endpoint => {
    const segment = flattenedBounds.find(_ref2 => {
      let {
        boundSegmentIndex
      } = _ref2;
      return boundSegmentIndex === activeSegment;
    });
    return segment ? segment[endpoint].city : '';
  };
  const handleToggleSeatSelect = (boundSegmentIndex, traveler) => () => {
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(traveler);
    setIsSummaryShowing(false);
  };
  const handleRemoveSeat = (segment, traveler) => () => {
    onRemove(segment, traveler);
  };
  const handleOnClose = data => {
    var _onClose;
    if (!onClose) {
      return;
    }
    analytics.pushToDataLayer([{
      event: 'seatingVisible',
      seatingSegment: 'BOOK'
    }, {
      event: 'seatingVisible',
      seatingOnBook: 'YES'
    }]);
    onClose === null || onClose === void 0 || (_onClose = onClose(data)) === null || _onClose === void 0 || _onClose();
  };
  const handleCloseButton = () => {
    handleOnClose(selectedSeats);
  };
  useEffect(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingCurrentSegment: activeSegment
    });
  }, [activeSegment]);
  analytics.useLogMouseFlowTagEffect('Seatmap 4.0');
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SeatMapHeader, {
      currentSegmentData: getSegment(flattenedBounds, activeSegment),
      currentSegmentIndex: findIndexOfSegment(flattenedBounds, activeSegment) + 1,
      from: getCurrentSegmentHeader('from'),
      handleCloseButton: handleCloseButton,
      to: getCurrentSegmentHeader('to'),
      totalSegmentsCount: flattenedBounds.length
    }), /*#__PURE__*/_jsxs(Stack, {
      children: [/*#__PURE__*/_jsx(AnimatePresence, {
        children: /*#__PURE__*/_jsx(motion.div, {
          animate: shouldAnimate ? {
            x: 0,
            opacity: 1
          } : false,
          exit: {
            x: '-100%',
            opacity: 0
          },
          initial: {
            x: '100%',
            opacity: 0
          },
          style: {
            height: '86%',
            display: 'flex',
            flexDirection: 'column'
          },
          transition: {
            type: 'tween',
            duration: 0.3
          },
          children: isSummaryShowing ? /*#__PURE__*/_jsx(SeatSummary, {
            getSelected: getSelected,
            onRemoveSeat: handleRemoveSeat,
            onToggleSeatSelect: handleToggleSeatSelect,
            segments: flattenedBounds,
            totalMarkup: totalMarkup,
            totalPrice: totalPrice
          }) : /*#__PURE__*/_jsx(SeatMapMainContent, {
            activeSegment: activeSegment,
            activeTraveler: activeTraveler,
            getSelected: getSelected,
            onChange: onChange,
            segments: flattenedBounds
          })
        }, `${activeSegment}-${isSummaryShowing}`)
      }), /*#__PURE__*/_jsx(Footer, {
        onClose: handleCloseButton,
        onSaveAndContinue: handleNextSegmentClick,
        shouldShowSummary: isSummaryShowing
      })]
    })]
  });
};
export default SeatMapContent;
//# sourceMappingURL=SeatMapContent.js.map